import { QueryPartStrategy } from '../../../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../../../advanced-search/filters/base/entities/query-part.class';
import { Combine, Restriction } from '../../../api/entities/api-query.entity';
import { Group, UserGroupType } from '../../../groups/entities/group.entity';
import { TaskKey } from '../../../tasks/entities/task-key.type';
import { User } from '../../../users/entities/user.entity';
import { Zone, ZoneId } from '../../../zones/entities/zone.entity';
import {
    DeleteStatus,
    FragmentId,
    MediaObjectId,
    MediaType,
    RecordId,
    RecordPhase,
    RecordStatus,
    RecordType,
    TernaryLogic
} from '../../entities/metadata/metadata.entity';

export class RecordRestrictions {
    public static notRecordTask(): Restriction {
        return new QueryPart({
            field: 'Workflow.Tasks.Task.TypeId',
            strategy: QueryPartStrategy.MatchNone,
            values: [TaskKey.EvaluateMedia, TaskKey.CorrectMedia]
        });
    }

    public static notPureFragment(): Restriction {
        return new QueryPart({
            field: 'Internal.IsFragment',
            strategy: QueryPartStrategy.MatchAll,
            values: ['false']
        });
    }

    public static uploadedByRestriction(user: User): Restriction {
        return new QueryPart({
            field: 'UploadedById',
            strategy: QueryPartStrategy.MatchAll,
            values: [user.Id]
        });
    }

    public static conceptZoneRestriction(zones: Zone[]): Restriction {
        return Combine.And(
            RecordRestrictions.phaseRestriction([RecordPhase.Concept]),
            RecordRestrictions.excludeRecordStatusesRestriction([RecordStatus.Uploaded, RecordStatus.Uploading]),
            RecordRestrictions.userZoneRestriction(zones)
        );
    }

    public static conceptRestrictionShare(): Restriction {
        return Combine.And(
            RecordRestrictions.phaseRestriction([RecordPhase.Concept]),
            RecordRestrictions.excludeRecordStatusesRestriction([RecordStatus.Uploaded, RecordStatus.Uploading])
        );
    }

    public static archivedPhaseRestriction(): Restriction {
        return RecordRestrictions.phaseRestriction([RecordPhase.Archived]);
    }

    public static excludeRecordStatusesRestriction(recordStatuses: RecordStatus[]): Restriction {
        return new QueryPart({
            field: 'RecordStatus',
            strategy: QueryPartStrategy.MatchNone,
            values: recordStatuses
        });
    }

    public static userZoneRestriction(zones: Zone[]): Restriction {
        const zoneIds: ZoneId[] = zones.map(zone => zone.Id);

        return new QueryPart({
            field: 'ZoneId',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: zoneIds
        });
    }

    public static nonDeletedZoneRestriction(): Restriction {
        return new QueryPart({
            field: 'DeleteStatus',
            strategy: QueryPartStrategy.MatchAll,
            values: [DeleteStatus.NotDeleted]
        }) as Restriction;
    }

    public static logicallyDeletedZoneRestriction(): Restriction {
        return new QueryPart({
            field: 'DeleteStatus',
            strategy: QueryPartStrategy.MatchAll,
            values: [DeleteStatus.LogicallyDeleted]
        }) as Restriction;
    }

    public static includeLogicallyDeletedRecordsRestriction(): Restriction {
        return new QueryPart({
            field: 'DeleteStatus',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: [DeleteStatus.NotDeleted, DeleteStatus.LogicallyDeleted]
        }) as Restriction;
    }

    public static onlyDeletedRecordsRestriction(zones: Zone[]): Restriction {
        return Combine.And(this.logicallyDeletedZoneRestriction(), RecordRestrictions.userZoneRestriction(zones));
    }

    public static excludeSubmittedRestriction(): Restriction {
        return new QueryPart({
            field: 'RecordStatus',
            strategy: QueryPartStrategy.MatchNone,
            values: [RecordStatus.Submitted]
        }) as Restriction;
    }

    public static phaseRestriction(phases: RecordPhase[]): Restriction {
        return new QueryPart({
            field: 'RecordPhase',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: phases
        });
    }

    public static noPhaseRestriction(): Restriction {
        return new QueryPart({
            field: 'RecordPhase',
            strategy: QueryPartStrategy.MatchAll,
            values: [TernaryLogic.both]
        });
    }

    public static publishedStatusRestriction(): Restriction {
        return new QueryPart({
            field: 'RecordStatus',
            strategy: QueryPartStrategy.MatchAll,
            values: [RecordStatus.Published]
        });
    }

    public static excludeConceptPhaseRestriction(): Restriction {
        return new QueryPart({
            field: 'RecordPhase',
            strategy: QueryPartStrategy.MatchNone,
            values: [RecordPhase.Concept]
        });
    }

    public static publishedPhaseRestriction(): Restriction {
        return this.phaseRestriction([RecordPhase.Published]);
    }

    public static uploadedRestriction(): Restriction {
        return new QueryPart({
            field: 'RecordStatus',
            strategy: QueryPartStrategy.MatchAll,
            values: [RecordStatus.Uploaded]
        });
    }

    public static processingRestriction(): Restriction {
        return new QueryPart({
            field: 'RecordStatus',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: [RecordStatus.Processing, RecordStatus.Rejected]
        });
    }

    public static excludeProcessingRestriction(): Restriction {
        return new QueryPart({
            field: 'RecordStatus',
            strategy: QueryPartStrategy.MatchNone,
            values: [RecordStatus.Processing, RecordStatus.Rejected]
        });
    }

    public static exportableRestriction(groups: Group[]): Restriction {
        const exportGroupValues: string[] = groups.map(group => group.Id);
        return new QueryPart({
            field: 'PermissionsExport',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: exportGroupValues
        });
    }

    public static nonExportableRestriction(groups: Group[]): Restriction {
        const exportGroupValues: string[] = groups.map(group => group.Id);

        return new QueryPart({
            field: 'PermissionsExport',
            strategy: QueryPartStrategy.MatchNone,
            values: exportGroupValues
        });
    }

    public static editableRestriction(groups: Group[]): Restriction {
        const writeGroupValues: string[] = groups.map(group => group.Id);
        return new QueryPart({
            field: 'PermissionsWrite',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: writeGroupValues
        });
    }

    public static publicRestriction(groups: Group[]): Restriction {
        const publicGroup: string = groups.find(group => group.Type === UserGroupType.Public)?.Id ?? '';

        return new QueryPart({
            field: 'PermissionsRead',
            strategy: QueryPartStrategy.MatchAll,
            values: [publicGroup]
        });
    }

    public static childOfParentRestriction(parentId: RecordId): Restriction {
        return new QueryPart({
            field: 'ParentRecordId',
            strategy: QueryPartStrategy.MatchAll,
            values: [parentId as any]
        });
    }

    public static candidateChildOfParentRestriction(parentId: FragmentId): Restriction {
        return new QueryPart({
            field: 'RelationsIsCandidateChildOf',
            strategy: QueryPartStrategy.MatchAll,
            values: [parentId as any]
        });
    }

    public static containedByRestriction(fragmentId: FragmentId): Restriction {
        return new QueryPart({
            field: 'RelationsContainedBy',
            strategy: QueryPartStrategy.MatchAll,
            values: [fragmentId as any]
        });
    }

    public static notInRelationRestriction(fragmentId: FragmentId, relationType: string): Restriction {
        return new QueryPart({
            field: 'Relations' + relationType,
            strategy: QueryPartStrategy.MatchNone,
            values: [fragmentId as any]
        });
    }

    public static fragmentOfMainFragmentRestriction(mainFragmentId: FragmentId): Restriction {
        return Combine.And(
            new QueryPart({
                field: 'MainFragment',
                strategy: QueryPartStrategy.MatchAll,
                values: [mainFragmentId]
            }),
            // Filter self
            new QueryPart({
                field: 'FragmentId',
                strategy: QueryPartStrategy.MatchNone,
                values: [mainFragmentId]
            })
        );
    }

    public static classificationRestriction(classification: MediaObjectId): Restriction {
        return new QueryPart({
            field: 'ReferenceCodesClassification',
            strategy: QueryPartStrategy.MatchAll,
            values: [classification as any]
        });
    }

    public static includeRecordTypeRestriction(recordType: RecordType): Restriction {
        return this.includeRecordTypesRestriction([recordType]);
    }

    public static includeRecordTypesRestriction(recordTypes: RecordType[]): Restriction {
        return new QueryPart({
            field: 'MainRecordType',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: recordTypes
        });
    }

    public static excludeMediaTypes(mediaTypes: MediaType[]): Restriction {
        return new QueryPart({
            field: 'Type',
            strategy: QueryPartStrategy.MatchNone,
            values: mediaTypes
        });
    }

    public static excludeFragmentByIdRestriction(fragmentId: FragmentId): Restriction {
        return new QueryPart({
            field: 'FragmentId',
            strategy: QueryPartStrategy.MatchNone,
            values: [fragmentId]
        });
    }

    public static geolocationRestriction(hasGeoLocation: TernaryLogic = TernaryLogic.both): Restriction {
        return new QueryPart({
            field: 'ContainsGeoData',
            strategy: QueryPartStrategy.MatchAll,
            values: [hasGeoLocation]
        });
    }

    public static representationsOfRecord(recordId: RecordId): Restriction {
        return Combine.And(
            this.includeRecordTypeRestriction(RecordType.Representation),
            new QueryPart({
                field: 'DataRecordId',
                strategy: QueryPartStrategy.MatchAll,
                values: [recordId]
            }),
            new QueryPart({
                field: 'Structural.Versioning.Status',
                strategy: QueryPartStrategy.MatchAll,
                values: [TernaryLogic.both]
            })
        );
    }

    public static includeAllRecordTypes(): Restriction {
        return new QueryPart({
            field: 'RecordType',
            strategy: QueryPartStrategy.MatchAll,
            values: ['*']
        });
    }
}
